import React, { useState } from 'react';
import { Modal, ModalHeader, ModalBody, LoadingIndicator, SimpleWizard, SimpleWizardStep } from '@ygtechnologies/bygr-frontend-component-library';
import { Trans } from '@lingui/react/macro';
import DocumentSelectList from '@/components/DocumentSelectList';
import { ButtonProps } from '@ygtechnologies/rubus-ui';
import { triggerHandler } from '@ygtechnologies/rubus-frontend-utilities';
import { searchResidentialUnitDocuments } from '@/services/documents';
import { ResidentialUnitDocument } from '@/types';
import { LargeRadio } from '@/components/forms/Radio';
import { FileType } from '@ygtechnologies/rubus-frontend-utilities';

interface OrderExportSelectSketchDialogProps {
    residentialUnitId: number;
    show: boolean;
    onClose(): void;
    onAccept(includeSketch: boolean, documentVersionId: number | null): void;
}

enum Step {
    SelectMode,
    SelectDocument,
}

enum ExportMode {
    Normal,
    AutoSelectFile,
    ManualFile,
}

const OrderExportSelectSketchDialog: React.FC<OrderExportSelectSketchDialogProps> = ({
    residentialUnitId,
    show,
    onAccept,
    onClose,
}) => {
    const [exportMode, setExportMode] = useState(ExportMode.Normal);
    const [currentStep, setCurrentStep] = useState(Step.SelectMode);
    const [selectedDocumentId, setSelectedDocumentId] = useState<string | null>(null);
    const [documents, setDocuments] = useState<ResidentialUnitDocument[] | null>(null);

    const handleOpen = async () => {
        setExportMode(ExportMode.Normal);
        setCurrentStep(Step.SelectMode);
        setSelectedDocumentId(null);
        setDocuments(null);

        var docs = await searchResidentialUnitDocuments({
            residentialUnitIds: [residentialUnitId],
        });

        setDocuments(docs.filter((x) => [FileType.Image, FileType.Pdf].includes(x.lastVersionFileMetadata.fileType)));
    };

    const handleCancelClick = () => {
        triggerHandler(onClose);
    };

    const handleAcceptClick = () => {
        let documentVersionId: number | null = null;

        if (selectedDocumentId) {
            var document = documents!.find((x) => x.documentId === selectedDocumentId);

            if (document) {
                documentVersionId = document.lastVersionId;
            }
        }

        if (exportMode === ExportMode.ManualFile && !documentVersionId) {
            return;
        }

        const includeSketch = exportMode !== ExportMode.Normal;
        triggerHandler(onAccept, includeSketch, documentVersionId);
    };

    const saveButtonProps: Partial<ButtonProps> = {
        children: <Trans>Export</Trans>,
        disabled: !selectedDocumentId && exportMode === ExportMode.ManualFile,
    };

    const steps = exportMode === ExportMode.ManualFile ? [Step.SelectMode, Step.SelectDocument] : [Step.SelectMode];

    return (
        <Modal size="lg" isOpen={show} toggle={handleCancelClick} onOpen={handleOpen}>
            <ModalHeader>
                <Trans>Export room form</Trans>
            </ModalHeader>
            <ModalBody>
                <SimpleWizard
                    steps={steps}
                    currentStep={currentStep}
                    onStepChange={setCurrentStep}
                    onCancel={handleCancelClick}
                    onSubmit={handleAcceptClick}
                    saveButtonProps={saveButtonProps}>
                    <SimpleWizardStep stepNumber={Step.SelectMode} header={<Trans>Choose export mode</Trans>}>
                        <LargeRadio
                            onChange={() => setExportMode(ExportMode.Normal)}
                            checked={exportMode === ExportMode.Normal}>
                            <Trans>
                                <strong>No attachment</strong>
                                The room form will include only the the list of orders.
                            </Trans>
                        </LargeRadio>

                        <LargeRadio
                            onChange={() => setExportMode(ExportMode.AutoSelectFile)}
                            checked={exportMode === ExportMode.AutoSelectFile}>
                            <Trans>
                                <strong>Pick automatically</strong>
                                On the end of the room form, you will have an image of the first architect sketch we find.
                            </Trans>
                        </LargeRadio>

                        <LargeRadio
                            onChange={() => setExportMode(ExportMode.ManualFile)}
                            checked={exportMode === ExportMode.ManualFile}>
                            <Trans>
                                <strong>Choose manually</strong>
                                Choose a file to have on the end of the room form file. Only PDFs and images are allowed.
                            </Trans>
                        </LargeRadio>
                    </SimpleWizardStep>
                    <SimpleWizardStep stepNumber={Step.SelectDocument} header={<Trans>Choose a document</Trans>}>
                        {documents ? (
                            <DocumentSelectList
                                documents={documents}
                                mode="document"
                                value={selectedDocumentId}
                                onSelectionChange={(value) => setSelectedDocumentId(value as string)}
                            />
                        ) : (
                            <LoadingIndicator />
                        )}
                    </SimpleWizardStep>
                </SimpleWizard>
            </ModalBody>
            {/* <ModalFooter>
                <Button color="tertiary" onClick={handleCancelClick}>
                    <Trans>Cancel</Trans>
                </Button>
                <Button disabled={!selectedDocumentId} color="primary" onClick={handleAcceptClick}>
                    <Trans>Export</Trans>
                </Button>
            </ModalFooter> */}
        </Modal>
    );
};

export default OrderExportSelectSketchDialog;
